import { Divider } from 'antd'
import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useCopyToClipboard } from 'react-use'

import { API } from '@/api'
import { ServerImageInfo, ServerSnapInfo } from '@/api/type-server.ts'
import CopyIcon from '@/assets/icons/copy.svg?react'
import { Loading, message, Trans } from '@/components'
import { Deployment } from '@/models/deployment.ts'
import { formatRamMB } from '@/utils/formatter.ts'

interface props {
  _deployment: Deployment
}

export default function DeploymentTarget({ _deployment }: props) {
  const [loading, setLoading] = useState(false)
  const [imageSpec, setImageSpec] = useState<ServerImageInfo>()
  const [snapSpec, setSnapSpec] = useState<ServerSnapInfo>()
  const [state, copyToClipboard] = useCopyToClipboard()

  const getImages = useCallback(
    (imageId: number) => {
      setLoading(true)
      API.getImageList({
        page: 1,
        per_page: 99,
      })
        .then((res) => {
          if (res.code === 0) {
            const image = res.images.find((im) => im.id === imageId)
            setImageSpec(image)
          }
        })
        .catch((error) => {
          console.error(error)
        })
        .finally(() => {
          setLoading(false)
        })
    },
    [_deployment.resID],
  )

  const getSnapshots = useCallback(
    (snapshotId: number) => {
      setLoading(true)
      API.getSnapList({
        page: 1,
        per_page: 99,
        domains: [-1],
      })
        .then((res) => {
          if (res.code === 0) {
            const snapshot = res.snapshots.find((snap) => Number(snap.id) === snapshotId)
            setSnapSpec(snapshot)
          }
        })
        .catch((error) => {
          console.error(error)
        })
        .finally(() => {
          setLoading(false)
        })
    },
    [_deployment.resID],
  )

  const memoizedDeployment = useMemo(() => _deployment, [_deployment.resID])

  useEffect(() => {
    if (memoizedDeployment) {
      const { fromSnapshotId, fromImageId } = memoizedDeployment.runtimeEnv.systemEnv

      if (fromImageId !== 0) {
        getImages(memoizedDeployment.runtimeEnv.systemEnv.fromImageId)
      }

      if (fromSnapshotId !== 0) {
        getSnapshots(memoizedDeployment.runtimeEnv.systemEnv.fromSnapshotId)
      }
    }
  }, [
    getImages,
    getSnapshots,
    memoizedDeployment.runtimeEnv.systemEnv.fromImageId,
    memoizedDeployment.runtimeEnv.systemEnv.fromSnapshotId,
  ])

  useEffect(() => {
    if (state.value) {
      message.success('Copied')
    }
  }, [state])

  return (
    <>
      {loading && (
        <div className="w-full flex items-center justify-center p-8">
          <Loading />
        </div>
      )}
      {!loading && (imageSpec || snapSpec) && (
        <div className="bg-[#F5F7FA] rounded-lg flex flex-wrap t-sm text-tertiary gap-y-4">
          <div className="w-full grid grid-cols-4 gap-y-4 rounded-lg">
            <div className="col-span-4 grid grid-cols-4 space-x-4">
              <div className="col-span-1 space-y-1">
                <div className="t-xs text-secondary">ID</div>
                <div className="t-sb text-primary">{_deployment.resID}</div>
              </div>
              <div className="col-span-1 space-y-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="deploy.deployTarget.name">Auto Deploy Name</Trans>
                </div>
                <div className="t-sb text-primary">{_deployment.name}</div>
              </div>
              <div className="col-span-2 space-y-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="deploy.deployTarget.maxNumberIns">
                    Maximum Number of Instances (Technical Preview)
                  </Trans>
                </div>
                <div className="t-sb text-primary">1</div>
              </div>
            </div>
            <div className="col-span-4 grid grid-cols-4 space-x-4">
              <div className="col-span-4 space-y-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="deploy.deployTarget.description">Auto Deploy Description</Trans>
                </div>
                <div className="t-sb text-primary flex space-x-1 items-center">
                  {_deployment.description}
                </div>
              </div>
            </div>
          </div>
          <div className="w-full t-xs text-tertiary flex space-x-2 items-center overflow-hidden">
            <div className="text-nowrap">
              <Trans i18nKey="deploy.deployTarget.instancePreview">Instance Preview</Trans>
            </div>
            <Divider className="my-0 pr-4" />
          </div>

          <div className="w-full grid grid-cols-4 gap-y-4 rounded-lg">
            {_deployment.runtimeEnv.systemEnv.fromImageId !== 0 && imageSpec && (
              <div className="col-span-4 grid grid-cols-4 space-x-4">
                <div className="col-span-1 space-y-1">
                  <div className="t-xs text-secondary">
                    <Trans i18nKey="deploy.deployTarget.image">Image</Trans>
                  </div>
                  <div className="t-sb text-primary">{imageSpec.alias}</div>
                </div>
                <div className="col-span-3 space-y-1">
                  <div className="t-xs text-secondary">
                    <Trans i18nKey="deploy.deployTarget.imageDescription">Image description</Trans>
                  </div>
                  <div className="t-sb text-primary">{imageSpec.description}</div>
                </div>
              </div>
            )}
            {_deployment.runtimeEnv.systemEnv.fromSnapshotId !== 0 && snapSpec && (
              <div className="col-span-4 grid grid-cols-4 space-x-4">
                <div className="col-span-1 space-y-1">
                  <div className="t-xs text-secondary">
                    <Trans i18nKey="deploy.deployTarget.snapshot">Snapshot</Trans>
                  </div>
                  <div className="t-sb text-primary">{snapSpec.flag.subject}</div>
                </div>
                <div className="col-span-3 space-y-1">
                  <div className="t-xs text-secondary">
                    <Trans i18nKey="deploy.deployTarget.snapshotCreatedTime">
                      Snapshot created time
                    </Trans>
                  </div>
                  <div className="t-sb text-primary">
                    {dayjs(snapSpec.flag.createTime).format('YYYY-MM-DD HH:mm:ss')}
                  </div>
                </div>
              </div>
            )}
            <div className="col-span-4 grid grid-cols-4 space-x-4">
              <div className="col-span-1 space-y-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="deploy.deployTarget.gpuModel">GPU Model</Trans>
                </div>
                <div className="t-sb text-primary">
                  {_deployment.runtimeEnv.hardwareEnv.resource.gpuName}
                </div>
              </div>
              <div className="col-span-1 space-y-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="deploy.deployTarget.gpus">GPUs</Trans>
                </div>
                <div className="t-sb text-primary">
                  {_deployment.runtimeEnv.hardwareEnv.resource.gpus}
                </div>
              </div>
              <div className="col-span-2 space-y-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="deploy.deployTarget.gpuRam">GPU RAM</Trans>
                </div>
                <div className="t-sb text-primary">
                  {formatRamMB(_deployment.runtimeEnv.hardwareEnv.resource.gpuRamMB)}
                </div>
              </div>
            </div>
            <div className="col-span-4 grid grid-cols-4 space-x-4">
              <div className="col-span-1 space-y-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="deploy.deployTarget.cpuModel">CPU Model</Trans>
                </div>
                <div className="t-sb text-primary">
                  {_deployment.runtimeEnv.hardwareEnv.resource.cpuName}
                </div>
              </div>
              <div className="col-span-1 space-y-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="deploy.deployTarget.vCpus">vCPUs</Trans>
                </div>
                <div className="t-sb text-primary">
                  {_deployment.runtimeEnv.hardwareEnv.resource.cpus}
                </div>
              </div>
              <div className="col-span-1 space-y-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="deploy.deployTarget.ram">RAM</Trans>
                </div>
                <div className="t-sb text-primary">
                  {formatRamMB(_deployment.runtimeEnv.hardwareEnv.resource.ramMB)}
                </div>
              </div>
              <div className="col-span-1 space-y-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="deploy.deployTarget.storage">Storage</Trans>
                </div>
                <div className="t-sb text-primary">
                  {_deployment.runtimeEnv.hardwareEnv.resource.diskGB} GB
                </div>
              </div>
            </div>
          </div>

          <div className="w-full t-xs text-tertiary flex space-x-2 items-center overflow-hidden">
            <div className="text-nowrap">
              <Trans i18nKey="deploy.deployTarget.serviceAndStartCommand">
                Service & Start Command
              </Trans>
            </div>
            <Divider className="my-0 pr-2" />
          </div>

          <div className="w-full grid grid-cols-4 gap-y-4 rounded-lg">
            <div className="col-span-4 grid grid-cols-4 space-x-4">
              <div className="col-span-1 space-y-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="deploy.deployTarget.accessMethodTitle">Access Method</Trans>
                </div>
                <div className="t-sb text-primary">
                  <Trans i18nKey="deploy.deployTarget.sharedMethod">Shared Method</Trans>
                </div>
              </div>
              <div className="col-span-2 space-y-1">
                <div className="t-xs text-secondary">URL</div>
                <div className="t-sb text-primary flex space-x-1 items-center">
                  <div>{_deployment.gateway.url}</div>
                  <div
                    className="cursor-pointer flex gap-x-1 items-center t-sm text-highlight"
                    onClick={() => {
                      copyToClipboard(_deployment.gateway.url)
                    }}
                  >
                    <CopyIcon width={16} height={16} />
                    Copy
                  </div>
                </div>
              </div>
              <div className="col-span-1 space-y-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="deploy.deployTarget.port">Port</Trans>
                </div>
                <div className="t-sb text-primary flex space-x-1 items-center">
                  {_deployment.gateway.port > 0 ? (
                    <>
                      <div>{_deployment.gateway.port}</div>
                      <div
                        className="cursor-pointer flex gap-x-1 items-center t-sm text-highlight"
                        onClick={() => {
                          copyToClipboard(_deployment.gateway.port.toString())
                        }}
                      >
                        <CopyIcon width={16} height={16} />
                        Copy
                      </div>
                    </>
                  ) : (
                    <>-</>
                  )}
                </div>
              </div>
            </div>
            <div className="col-span-4 grid grid-cols-4 space-x-4">
              <div className="col-span-4 space-y-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="deploy.deployTarget.cmd">Start Command</Trans>
                </div>
                <div className="t-sb text-primary flex space-x-1 items-center">
                  {_deployment.runtimeEnv.systemEnv.cmd.length > 0 ? (
                    <>
                      <div>{_deployment.runtimeEnv.systemEnv.cmd}</div>
                      <div
                        className="cursor-pointer flex gap-x-1 items-center t-sm text-highlight"
                        onClick={() => {
                          copyToClipboard(_deployment.runtimeEnv.systemEnv.cmd)
                        }}
                      >
                        <CopyIcon width={16} height={16} />
                        Copy
                      </div>
                    </>
                  ) : (
                    <>-</>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
